import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FloppyDisk, Trash, Stack } from "@phosphor-icons/react";

export const SopBundleBar = ({ selection, onApply, apiBase = "/sop" }) => {
  const [presets, setPresets] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [picked, setPicked] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadBundles = () => api.get(`${apiBase}/bundles`).then((r) => setBundles(r.data.bundles || [])).catch(() => setBundles([]));
  useEffect(() => {
    api.get(`${apiBase}/bundle-presets`).then((r) => setPresets(r.data.presets || [])).catch(() => {});
    loadBundles();
  }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (val) => {
    setPicked(val);
    const [kind, id] = val.split(":");
    if (kind === "preset") {
      const p = presets.find((x) => x.key === id);
      if (p) { onApply(p.requirement_ids, {}, p.standard_ids); toast.success(`Loaded preset: ${p.name}`); }
    } else {
      const b = bundles.find((x) => x.id === id);
      if (b) { onApply(b.requirement_ids, b.part_selections || {}, []); setName(b.name); toast.success(`Loaded bundle: ${b.name}`); }
    }
  };

  const ids = Object.keys(selection);
  const current = picked.startsWith("bundle:") ? bundles.find((b) => `bundle:${b.id}` === picked) : null;

  const save = async () => {
    if (!name.trim()) return toast.error("Name the bundle");
    if (!ids.length) return toast.error("Select at least one requirement");
    setSaving(true);
    try {
      const payload = { name, requirement_ids: ids, part_selections: selection };
      const { data } = current && current.name === name.trim()
        ? await api.put(`${apiBase}/bundles/${current.id}`, payload)
        : await api.post(`${apiBase}/bundles`, payload);
      await loadBundles();
      setPicked(`bundle:${data.id}`);
      toast.success("Bundle saved for this entity");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!current) return;
    try {
      await api.delete(`${apiBase}/bundles/${current.id}`);
      setPicked(""); setName(""); await loadBundles();
      toast.success("Bundle deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2" data-testid="sop-bundle-bar">
      <Select value={picked} onValueChange={apply}>
        <SelectTrigger data-testid="sop-bundle-select" className="rounded-none"><Stack size={14} className="mr-1 shrink-0" /><SelectValue placeholder="Start from a preset or saved bundle…" /></SelectTrigger>
        <SelectContent className="max-h-80">
          <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Related-standard presets</div>
          {presets.map((p) => <SelectItem key={p.key} value={`preset:${p.key}`} data-testid={`sop-preset-${p.key}`}>{p.name} ({p.standard_ids.join(", ")})</SelectItem>)}
          {bundles.length > 0 && <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Saved for this entity</div>}
          {bundles.map((b) => <SelectItem key={b.id} value={`bundle:${b.id}`} data-testid={`sop-bundle-${b.id}`}>{b.name} · {b.requirement_ids.length} reqs</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-1.5">
        <Input data-testid="sop-bundle-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bundle name (to save selection)" className="rounded-none h-8 text-xs" />
        <Button data-testid="sop-bundle-save-btn" size="sm" variant="outline" onClick={save} disabled={saving} className="rounded-none h-8 shrink-0" title="Save this selection as a reusable bundle (this entity only)">
          <FloppyDisk size={14} />
        </Button>
        {current && (
          <Button data-testid="sop-bundle-delete-btn" size="sm" variant="outline" onClick={remove} className="rounded-none h-8 shrink-0 text-red-700" title="Delete this saved bundle">
            <Trash size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};
